import { useEffect, useRef } from "react";
import { media } from "@/lib/api";
import { OverlayLayer, boxStyle, newBox } from "@/components/Overlays";

export const OVERFLOW_LIMIT = (layout) => {
  if (layout.image_pos === "full") return Infinity;
  const imgH = layout.image_pos === "top" || layout.image_pos === "bottom" ? layout.image_height || 55 : 0;
  const factor = (16 / (layout.size || 16)) ** 2 * (1.5 / (layout.line_height || 1.5));
  const sideFactor = layout.image_pos === "left" || layout.image_pos === "right" ? 0.5 : 1;
  return Math.round(((100 - imgH) / 100) * 2000 * factor * sideFactor);
};

function EditableText({ text, onText }) {
  const ref = useRef();
  useEffect(() => { if (ref.current && document.activeElement !== ref.current && ref.current.innerText !== text) ref.current.innerText = text; }, [text]);
  return <div ref={ref} contentEditable suppressContentEditableWarning onInput={(e) => onText(e.currentTarget.innerText)} className="outline-none whitespace-pre-wrap min-h-full cursor-text hover:bg-[#1a6fd0]/[.03] focus:bg-[#1a6fd0]/[.04] rounded" data-testid="canvas-text-editable" />;
}

export function PageCanvas({ page, number, className = "", highlight, scale = 1, editable, onText, onLayout, onOverlays, selected, onSelect }) {
  const root = useRef();
  if (!page) return null;
  const L = page.layout || {};
  const pos = L.image_pos || "top";
  const full = pos === "full";
  const resize = (e) => {
    e.preventDefault();
    const r = root.current.getBoundingClientRect();
    const mv = (ev) => { const y = ((ev.clientY - r.top) / r.height) * 100; onLayout?.("image_height", Math.round(Math.max(20, Math.min(85, pos === "bottom" ? 100 - y : y)))); };
    const up = () => { window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", mv); window.addEventListener("pointerup", up);
  };
  const img = page.image_url && pos !== "none" && !full ? (
    <div className={`relative overflow-hidden ${pos === "left" || pos === "right" ? "w-1/2 h-full" : "w-full"}`} style={pos === "top" || pos === "bottom" ? { height: `${L.image_height || 55}%` } : {}}>
      <img src={media(page.image_url)} alt="" className="w-full h-full object-cover" style={{ transform: `scale(${(L.image_size || 100) / 100})` }} />
      {editable && (pos === "top" || pos === "bottom") && <span onPointerDown={resize} title="Sürükleyerek görsel yüksekliğini değiştirin" className={`absolute inset-x-0 ${pos === "top" ? "bottom-0" : "top-0"} h-2 cursor-ns-resize bg-[#1a6fd0]/0 hover:bg-[#1a6fd0]/60`} data-testid="canvas-image-resize" />}
    </div>
  ) : null;
  const paras = (page.text || "").split(/\n\s*\n/);
  const limit = OVERFLOW_LIMIT(L);
  const text = full ? null : (
    <div className="flex-1 overflow-hidden px-[7%] py-[5%] font-lora" style={{ fontFamily: `'${L.font || "Lora"}', serif`, fontSize: `${(L.size || 16) * 0.9 * scale}px`, lineHeight: L.line_height || 1.5, textAlign: L.align || "justify", color: L.color || "#0F2F33" }}>
      {editable ? <EditableText text={page.text || ""} onText={onText} /> : paras.map((p, i) => (
        <p key={i} className="mb-[0.7em] indent-[1.2em]">
          {highlight && p.includes(highlight) ? <>{p.split(highlight)[0]}<mark className="bg-[#cfe3fb] outline-dashed outline-1 outline-[#4b8be0]">{highlight}</mark>{p.split(highlight).slice(1).join(highlight)}</> : p}
        </p>
      ))}
    </div>
  );
  const row = pos === "left" || pos === "right";
  return (
    <div ref={root} className={`relative aspect-[210/297] overflow-hidden shadow-[0_10px_30px_rgba(19,41,75,.14)] ${className}`} style={{ background: L.bg || "#FBF7EE" }} data-testid="page-canvas">
      {full && page.image_url && <img src={media(page.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ transform: `scale(${(L.image_size || 100) / 100})` }} data-testid="canvas-full-image" />}
      <div className={`absolute inset-0 flex ${row ? "flex-row" : "flex-col"} ${pos === "bottom" || pos === "right" ? (row ? "flex-row-reverse" : "flex-col-reverse") : ""}`}>
        {img}{text}
      </div>
      {full && !(page.overlays || []).length && page.text && <div className="absolute pointer-events-none whitespace-pre-wrap overflow-hidden" style={boxStyle({ ...newBox(page.text), font: L.font, size: L.size || 16, bg_opacity: 45, y: 70, h: 24 }, 1)} data-testid="canvas-full-fallback-text">{page.text}</div>}
      <OverlayLayer boxes={page.overlays || []} onChange={onOverlays || (() => {})} selected={selected} onSelect={onSelect} editable={editable} prefix="page-ov" />
      {(page.text || "").length > limit && <span className="absolute bottom-8 right-4 w-5 h-5 rounded-full bg-red-600 text-white text-xs grid place-items-center font-bold" title="Metin taşıyor">!</span>}
      <div className={`absolute bottom-2 inset-x-0 text-center text-xs font-lora pointer-events-none ${full ? "text-white/80" : "text-[#7a6e62]"}`}>~ {number ?? page.order} ~</div>
    </div>
  );
}
